import { BUY_PAGE } from '../../constants/pages';
import { LANDING_PAGE } from '../../constants/sessionStorage';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from './sessionStorageHelper';
import { storePriceAlertDeepLinkFromSearch } from './priceAlertDeepLinkHelper';

// Values written via storeLandingPage() are base64-encoded and tagged with this
// prefix so the raw path + query string isn't human-readable in devtools. NOTE:
// base64 is obfuscation, NOT encryption — it's trivially reversible (the same
// path is also visible in the URL bar). The prefix lets decodeLandingPage()
// deterministically distinguish an encoded value from a raw one, so LANDING_PAGE
// values written elsewhere (as raw strings) still resolve correctly.
const ENCODED_PREFIX = 'b64:';

// encodeURIComponent guards btoa against non-Latin1 characters in the path.
const encodeLandingPage = (path) => {
  try {
    return `${ENCODED_PREFIX}${btoa(encodeURIComponent(path))}`;
  } catch {
    return path;
  }
};

const decodeLandingPage = (value) => {
  if (typeof value !== 'string' || !value.startsWith(ENCODED_PREFIX)) {
    // Raw (unencoded) value — return unchanged.
    return value;
  }
  try {
    return decodeURIComponent(atob(value.slice(ENCODED_PREFIX.length)));
  } catch {
    return value;
  }
};

export const storeLandingPage = (path) => {
  storeInSessionStorage(LANDING_PAGE, encodeLandingPage(path));
};

// AuthRoute (render) and login pages (effects) can both call this in the same
// turn after JWT is set. Remember the value briefly so the second caller does
// not fall through to BUY_PAGE and wipe a deep-link redirect.
let consumedLandingPage = null;

export const landingPageHelper = (fallback = BUY_PAGE) => {
  const landingPage = retrieveFromSessionStorage(LANDING_PAGE);

  if (landingPage) {
    const page = decodeLandingPage(landingPage);
    removeFromSessionStorage(LANDING_PAGE);

    // If the saved path carries a price-alert deep link, seed it into session
    // storage and redirect to the CLEAN path instead. Otherwise AuthRoute would
    // redirect to the full `/buy?openAlerts=…&productCode=…` URL and the query
    // would flash in the address bar until BuyPage's mount effect stripped it.
    const queryIndex = page.indexOf('?');
    const matched =
      queryIndex !== -1 &&
      storePriceAlertDeepLinkFromSearch(page.slice(queryIndex));
    const resolvedPage = matched ? page.slice(0, queryIndex) : page;

    consumedLandingPage = resolvedPage;
    setTimeout(() => {
      consumedLandingPage = null;
    }, 0);
    return resolvedPage;
  }

  if (consumedLandingPage) {
    const page = consumedLandingPage;
    consumedLandingPage = null;
    return page;
  }

  return fallback;
};

// Splits a stored landing path (may include ?query) into a location descriptor
// so callers can safely use history.push({ pathname, search, state }).
export const getLandingPageLocation = (fallback = BUY_PAGE) => {
  const landingPage = landingPageHelper(fallback);
  const questionIndex = landingPage.indexOf('?');

  if (questionIndex === -1) {
    return { pathname: landingPage };
  }

  return {
    pathname: landingPage.slice(0, questionIndex),
    search: landingPage.slice(questionIndex),
  };
};
